/**
 * @file     PlaceHeading.tsx
 * @purpose  Text of the /properties H1 read from the URL `region` tokens, so a
 *           city hub link lands on "Homes for sale in Kelowna". Renders inside
 *           the server H1 under a Suspense whose fallback is the BC heading.
 *           In Rent mode it reads "Rentals in Kelowna".
 */
'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { usePanelMode } from './PanelIntroMode';

type Translate = (key: string, options: Record<string, string | number>) => string;

/** Heading for the named places, or null when the URL names none. */
export function placesHeading(places: readonly string[], t: Translate, mode: 'buy' | 'rent' = 'buy'): string | null {
  if (places.length === 0) return null;
  if (mode === 'rent') {
    if (places.length === 1) {
      return t('pages.properties.seo.rentHeadingPlace', { defaultValue: 'Rentals in {{place}}', place: places[0] });
    }
    if (places.length === 2) {
      return t('pages.properties.seo.rentHeadingTwoPlaces', {
        defaultValue: 'Rentals in {{first}} and {{second}}',
        first: places[0],
        second: places[1],
      });
    }
    return t('pages.properties.seo.rentHeadingManyPlaces', { defaultValue: 'Rentals in {{n}} areas', n: places.length });
  }
  if (places.length === 1) {
    return t('pages.properties.seo.headingPlace', { defaultValue: 'Homes for sale in {{place}}', place: places[0] });
  }
  if (places.length === 2) {
    return t('pages.properties.seo.headingTwoPlaces', {
      defaultValue: 'Homes for sale in {{first}} and {{second}}',
      first: places[0],
      second: places[1],
    });
  }
  return t('pages.properties.seo.headingManyPlaces', { defaultValue: 'Homes for sale in {{n}} areas', n: places.length });
}

export function PlaceHeading({ fallback, rentFallback }: { fallback: string; rentFallback?: string }) {
  const { t } = useTranslation();
  const panelMode = usePanelMode();
  const mode = rentFallback != null ? panelMode : 'buy';
  const searchParams = useSearchParams();
  const tokensKey = (searchParams?.getAll('region') ?? []).join('\n');
  const [places, setPlaces] = useState<string[]>([]);
  // The place tables (~40KB gz) load only for a URL that names regions; the
  // plain /properties heading needs none of them.
  useEffect(() => {
    const tokens = tokensKey ? tokensKey.split('\n') : [];
    if (tokens.length === 0) {
      setPlaces([]);
      return;
    }
    let cancelled = false;
    void import('@/core/config/places').then(({ regionTokenPlaces }) => {
      if (!cancelled) setPlaces(regionTokenPlaces(tokens));
    });
    return () => {
      cancelled = true;
    };
  }, [tokensKey]);
  return <>{placesHeading(places, t as Translate, mode) ?? (mode === 'rent' ? rentFallback : fallback)}</>;
}
